import React from "react";
import { Box, Button, Grid, Skeleton, Stack, Typography } from "@mui/material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import {
  SectionPanel,
  StatusPill,
  CodeChip,
  ClinicalEmptyState,
  tokens,
} from "../../../shared/clinical-design";

const Row = ({ label, value }) => (
  <Stack direction="row" spacing={1} sx={{ py: 0.4 }}>
    <Typography sx={{ fontSize: 12.5, color: tokens.ink3, minWidth: 110 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, color: tokens.ink, fontWeight: 500 }}>{value || "—"}</Typography>
  </Stack>
);

/**
 * CDCI 4.2 — Contexto del Paciente (Paso 2 del journey).
 * Todo lo que el médico necesita antes de entrar al cuarto, en una vista:
 * demografía y seguros, alergias, medicamentos, labs y notas previas.
 * Fuente futura: GET api/amd/encounters/{appointmentId}/context (agregador).
 */
export default function ContextTab({ loading, context, note, onStartNote }) {
  if (loading || !context) {
    return (
      <Grid container spacing={1.5}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={i}>
            <Skeleton variant="rounded" height={160} />
          </Grid>
        ))}
      </Grid>
    );
  }

  const { patient, appointment } = context;

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
        <Button
          variant="contained"
          startIcon={<PostAddIcon />}
          onClick={onStartNote}
          sx={{
            bgcolor: tokens.primary,
            "&:hover": { bgcolor: tokens.primary700 },
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {note ? "Continue note" : "Start note"}
        </Button>
      </Stack>

      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <SectionPanel title="Demographics & Insurance" sx={{ height: "100%" }}>
            <Row label="DOB" value={`${patient.dob} (${patient.age}y)`} />
            <Row label="Gender" value={patient.gender} />
            <Row label="Membership" value={patient.membership} />
            <Row label="Primary plan" value={patient.insurance?.primary} />
            <Row label="Member ID" value={patient.insurance?.memberId} />
            <Stack direction="row" spacing={1} sx={{ pt: 0.5 }} alignItems="center">
              <Typography sx={{ fontSize: 12.5, color: tokens.ink3, minWidth: 110 }}>Eligibility</Typography>
              <StatusPill
                tone={patient.insurance?.eligibility === "Active" ? "ok" : "crit"}
                label={patient.insurance?.eligibility || "Unknown"}
                dense
              />
            </Stack>
          </SectionPanel>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <SectionPanel title="Allergies" sx={{ height: "100%" }}>
            {patient.allergies?.length ? (
              patient.allergies.map((a) => (
                <Stack key={a.allergen} direction="row" spacing={1} alignItems="center" sx={{ py: 0.4 }}>
                  <StatusPill
                    tone={a.severity === "Severe" ? "crit" : a.severity === "Moderate" ? "warn" : "muted"}
                    label={a.severity}
                    dense
                  />
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{a.allergen}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: tokens.ink3 }}>{a.reaction}</Typography>
                </Stack>
              ))
            ) : (
              <ClinicalEmptyState message="No known allergies (NKDA)." sx={{ py: 1 }} />
            )}
          </SectionPanel>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <SectionPanel title="Active Problems" sx={{ height: "100%" }}>
            {patient.activeProblems?.map((p) => {
              const [code, ...rest] = p.split(" — ");
              return (
                <Stack key={p} direction="row" spacing={1} alignItems="center" sx={{ py: 0.4 }}>
                  <CodeChip code={code} />
                  <Typography sx={{ fontSize: 13 }}>{rest.join(" — ")}</Typography>
                </Stack>
              );
            })}
          </SectionPanel>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <SectionPanel title="Medications" sx={{ height: "100%" }}>
            {patient.medications?.length ? (
              patient.medications.map((m) => (
                <Box key={m.name} sx={{ py: 0.4 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{m.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.ink3 }}>
                    {m.sig} · last fill {m.lastFill}
                  </Typography>
                </Box>
              ))
            ) : (
              <ClinicalEmptyState message="No active medications on file." sx={{ py: 1 }} />
            )}
          </SectionPanel>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <SectionPanel title="Recent Labs" sx={{ height: "100%" }}>
            {patient.labs?.length ? (
              patient.labs.map((l) => (
                <Stack key={`${l.test}-${l.date}`} direction="row" spacing={1} alignItems="center" sx={{ py: 0.4 }}>
                  <Typography sx={{ fontSize: 13, minWidth: 110, fontWeight: 500 }}>{l.test}</Typography>
                  <Typography
                    sx={{
                      fontFamily: tokens.fontMono,
                      fontSize: 13,
                      color: l.flag === "H" ? tokens.crit : l.flag === "L" ? tokens.warn : tokens.ink,
                    }}
                  >
                    {l.value} {l.flag && `(${l.flag})`}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: tokens.ink3 }}>{l.date}</Typography>
                </Stack>
              ))
            ) : (
              <ClinicalEmptyState message="No recent lab results." sx={{ py: 1 }} />
            )}
          </SectionPanel>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <SectionPanel title="Previous Notes" sx={{ height: "100%" }}>
            {patient.previousNotes?.length ? (
              patient.previousNotes.map((n) => (
                <Stack key={`${n.date}-${n.template}`} direction="row" spacing={1} alignItems="center" sx={{ py: 0.4 }}>
                  <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12.5, color: tokens.ink2 }}>
                    {n.date}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{n.template}</Typography>
                  <StatusPill tone={n.signed ? "ok" : "warn"} label={n.signed ? "Signed" : "Draft"} dense />
                </Stack>
              ))
            ) : (
              <ClinicalEmptyState message="No previous notes for this patient." sx={{ py: 1 }} />
            )}
          </SectionPanel>
        </Grid>

        <Grid size={12}>
          <SectionPanel title="Visit">
            <Stack direction="row" spacing={4} flexWrap="wrap">
              <Row label="Location" value={appointment.location?.name} />
              <Row label="Address" value={appointment.location?.address} />
              <Row label="POS" value={appointment.location?.pos} />
              <Row label="Provider" value={appointment.provider?.name} />
            </Stack>
          </SectionPanel>
        </Grid>
      </Grid>
    </Box>
  );
}
